import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { api } from '../api/client';

type Reclamo = {
  id: number;
  nro_expediente: string;
  titulo: string;
  estado: string;
  categoria: string;
  categoria_icono: string;
  area: string;
  creado_en: string;
  calificacion: number | null;
  calificacion_cerrada: number;
};

const STATUS_LABEL: Record<string, string> = {
  pendiente:  'Pendiente',
  en_proceso: 'En proceso',
  resuelto:   'Resuelto',
  cerrado:    'Resuelto',
  rechazado:  'Rechazado',
};

const STATUS_CLS: Record<string, string> = {
  pendiente:  'bg-amber-100 text-amber-700',
  en_proceso: 'bg-blue-100 text-blue-700',
  resuelto:   'bg-green-100 text-green-700',
  cerrado:    'bg-green-100 text-green-700',
  rechazado:  'bg-red-100 text-red-700',
};

export default function Reclamos() {
  const nav = useNavigate();
  const [reclamos, setReclamos] = useState<Reclamo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setErr(null);
    try {
      const res = await api<{ items: Reclamo[] }>('/reclamos');
      setReclamos(
        [...res.items].sort((a, b) => new Date(b.creado_en).getTime() - new Date(a.creado_en).getTime())
      );
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <>
      <TopBar title="Mis reclamos" back />
      <main className="px-4 py-4 max-w-md mx-auto pb-24">

        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3 mb-4">{err}</div>
        )}

        {cargando && <p className="text-center text-gray-400 mt-10">Cargando…</p>}

        {!cargando && reclamos.length === 0 && (
          <div className="text-center pt-16">
            <p className="text-4xl mb-3">📭</p>
            <p className="text-gray-500 text-sm">No tenés reclamos aún</p>
          </div>
        )}

        <div className="space-y-3">
          {reclamos.map((r) => {
            const cls         = STATUS_CLS[r.estado] ?? STATUS_CLS.pendiente;
            const estaResuelto = r.estado === 'resuelto' || r.estado === 'cerrado';
            const sinCalificar = estaResuelto && r.calificacion == null && !r.calificacion_cerrada;

            return (
              <button
                key={r.id}
                onClick={() => nav(`/reclamos/${r.id}`)}
                className="w-full text-left bg-white border border-app rounded-xl p-4 shadow-sm active:bg-gray-50"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-gray-400 text-sm font-mono">{r.nro_expediente}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.calificacion != null && (
                      <span className="text-amber-400 text-sm">
                        {'★'.repeat(r.calificacion)}{'☆'.repeat(5 - r.calificacion)}
                      </span>
                    )}
                    <span className={`text-sm font-semibold px-2.5 py-0.5 rounded-full ${cls}`}>
                      {STATUS_LABEL[r.estado] ?? r.estado}
                    </span>
                  </div>
                </div>
                <p className="font-bold text-gray-900 text-base mt-2 leading-snug">{r.titulo}</p>
                <p className="text-gray-500 text-sm mt-1">
                  {r.categoria_icono} {r.categoria}{r.area ? ` · ${r.area}` : ''}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-gray-400 text-sm">
                    {new Date(r.creado_en).toLocaleDateString('es-AR')}
                  </span>
                  {sinCalificar && (
                    <span className="text-amber-500 text-sm font-bold">⭐ Tocá para calificar</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </main>
    </>
  );
}
